import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import {
  VisibilityOffOutlined,
  VisibilityOutlined,
  ArrowForward
} from '@mui/icons-material';

// Styles
import {
  BoxForm,
  BorderBoxForm,
  ButtonLogin,
  Logo,
  useStyles
} from '../../login/styles';
import * as S from './styles';
import { useTheme } from '@mui/material/styles';
import { getSecondaryColors } from '@/main/style/styleMaterialUIGlobal';

// Utils
import {
  getParamsToGetTranslates,
  getTranslateByKey,
  isNullUndefined,
  isNullUndefinedEmpty,
  isObjectEmpty,
  isSubDomainQualitas
} from '@/main/services/utils';
import { RoutesConstants } from '@/main/routes/routesConstants';

// Components
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import KogInputTextLogin from '@/main/components/shared/inputText/inputTextLogin';

// Hooks
import { useResetPassword } from '@/main/hooks/accountResetPasswordHook';
import { useTranslateHook } from '@/main/hooks/translateHook';
import ConfirmPasswordScreenQualitas from './qualitas/confirmPasswordScreenQualitas';

let redirectRouteValue: string = '';
function ConfirmPasswordScreen() {
  const location = useLocation();
  const styles = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const secondaryColors = getSecondaryColors(theme);
  const [searchParams] = useSearchParams();

  // Get params of URL
  const token = searchParams.get('token');
  const userId = searchParams.get('userId');

  const {
    isLoadingTranslates,
    translatesLocal,
    getTranslates
  } = useTranslateHook({
    fillTranslateLocal: true
  });
  const formResetPassword = useResetPassword({
    onSuccess: () => { },
    redirectRoute: redirectRouteValue
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(
    false
  );

  // Handles
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  // Function renders
  const renderErrors = () => {
    let errors = [];
    if (!formResetPassword.errors.newPassword?.types) return '';
    if (formResetPassword.errors.newPassword?.types?.required)
      errors.push(
        formResetPassword.errors.newPassword.types.required.toString()
      );
    else {
      errors = Object.values(formResetPassword.errors.newPassword.types);
    }

    return errors.map((error, index) => (
      <React.Fragment key={`error-message-${error}`}>
        {index > 0 && <br />}
        {error}
      </React.Fragment>
    ));
  };

  function renderContent() {
    if (isLoadingTranslates) {
      return <LoadingComponent />;
    }

    return (
      <S.Form onSubmit={formResetPassword.submit}>
        <KogInputTextLogin
          autoFocus
          id="txt-recovery-pwd-newpassword"
          className={styles.inputTextCustom}
          variant="outlined"
          label={getTranslateByKey(
            'information.newPassword',
            false,
            translatesLocal
          )}
          helperText={renderErrors()}
          error={!!formResetPassword.errors.newPassword}
          positionIcon="end"
          type={showPassword ? 'text' : 'password'}
          icon={
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              edge="end">
              {showPassword ? (
                <VisibilityOutlined
                  style={{ color: secondaryColors.contrastText }}
                />
              ) : (
                <VisibilityOffOutlined
                  style={{ color: secondaryColors.contrastText }}
                />
              )}
            </IconButton>
          }
          title={getTranslateByKey(
            'information.newPassword',
            false,
            translatesLocal
          )}
          {...formResetPassword.register('newPassword', {
            required: getTranslateByKey(
              'information.newPasswordIsRequired',
              true,
              translatesLocal
            ),
            minLength: {
              value: 16,
              message: getTranslateByKey(
                'information.mustHaveAtLeast16Digits',
                true,
                translatesLocal
              )
            },
            maxLength: {
              value: 50,
              message: getTranslateByKey(
                'information.mustNotBeLongerThan50Digits',
                true,
                translatesLocal
              )
            },
            validate: {
              containsUpperCaseLetter: (value: string) =>
                /[A-Z]/.test(value) ||
                getTranslateByKey(
                  'information.mustContainAtLeastOneUppercaseLetter',
                  true,
                  translatesLocal
                ),
              containsLowerCaseLetter: (value: string) =>
                /[a-z]/.test(value) ||
                getTranslateByKey(
                  'information.mustContainAtLeastOnelowercaseLetter',
                  true,
                  translatesLocal
                ),
              containsDigit: (value: string) =>
                /\d/.test(value) ||
                getTranslateByKey(
                  'information.mustContainAtLeastOneNumericCharacter',
                  true,
                  translatesLocal
                ),
              containsSpecialCharacter: (value: string) =>
                /\W/.test(value) ||
                getTranslateByKey(
                  'information.mustContainAtLeastOneSpecialCharacter',
                  true,
                  translatesLocal
                )
            }
          })}
        />
        <KogInputTextLogin
          id="txt-recovery-pwd-newpassowrd-confirm"
          className={styles.inputTextCustom}
          variant="outlined"
          label={getTranslateByKey(
            'information.confirmNewPassword',
            false,
            translatesLocal
          )}
          helperText={
            formResetPassword.errors.confirmPassword?.message ||
            formResetPassword.serverErrors
          }
          error={
            !!formResetPassword.errors.confirmPassword ||
            !!formResetPassword.serverErrors
          }
          positionIcon="end"
          type={showConfirmPassword ? 'text' : 'password'}
          icon={
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowConfirmPassword}
              edge="end">
              {showConfirmPassword ? (
                <VisibilityOutlined
                  style={{ color: secondaryColors.contrastText }}
                />
              ) : (
                <VisibilityOffOutlined
                  style={{ color: secondaryColors.contrastText }}
                />
              )}
            </IconButton>
          }
          title={getTranslateByKey(
            'information.confirmNewPassword',
            false,
            translatesLocal
          )}
          InputLabelProps={{
            style: {
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              width: 175,
              whiteSpace: 'nowrap'
            }
          }}
          {...formResetPassword.register('confirmPassword', {
            required: getTranslateByKey(
              'information.confirmNewPasswordRequired',
              true,
              translatesLocal
            ),
            validate: value =>
              value === formResetPassword.getValues('newPassword') ||
              getTranslateByKey(
                'information.passwordMustBeEquals',
                true,
                translatesLocal
              )
          })}
        />
        <ButtonLogin
          type="submit"
          disabled={formResetPassword.submitInProgress}
          style={{ margin: 0 }}>
          {getTranslateByKey('information.save', false, translatesLocal)}
          <ArrowForward fontSize="small" />
        </ButtonLogin>
      </S.Form>
    );
  }

  useEffect(() => {
    const { idCultureUser, lastSyncTranslations } = getParamsToGetTranslates();
    getTranslates({ culture: idCultureUser, lastSyncTranslations });
  }, []);

  useEffect(() => {
    formResetPassword.setValue('token', token);
    formResetPassword.setValue('userId', userId);
  }, [token, userId, formResetPassword]);

  useEffect(() => {
    if (formResetPassword.isSuccess) {
      toast.success(
        getTranslateByKey(
          'information.passwordChangedSuccessfully',
          false,
          translatesLocal
        )
      );

      if (isNullUndefinedEmpty(redirectRouteValue)) {
        navigate(RoutesConstants.login);
      }
    }
  }, [formResetPassword.isSuccess]);

  // Reset password by ModalUserChangePasswordAlert
  useEffect(() => {
    if (
      !isNullUndefined(location.state) &&
      !isObjectEmpty(location.state) &&
      !isNullUndefinedEmpty(location.state.redirectRoute)
    ) {
      redirectRouteValue = location.state.redirectRoute;
    }
  }, [location.state]);

  const isSubDomainQualitasValue = isSubDomainQualitas();

  if (isSubDomainQualitasValue) {
    return (
      <ConfirmPasswordScreenQualitas
        translatesLocal={translatesLocal}
        isLoadingTranslates={isLoadingTranslates}
        formResetPassword={formResetPassword}
      />
    );
  }

  return (
    <div id="container" className={styles.container}>
      <BoxForm>
        <Logo />
        {renderContent()}
      </BoxForm>
      <BorderBoxForm />
    </div>
  );
}

export default ConfirmPasswordScreen;
